import { Dialog } from "@headlessui/react"
import { FC, useEffect, useState } from "react"
import { KButton, KInput, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { lang } from "../../../../../constants/languages"
//@ts-ignore
import BulkUploadIconIcon from "../../../../../assets/release-registry-icons/bulk_upload.png"
//@ts-ignore
import GoogleDriveIconIcon from "../../../../../assets/release-registry-icons/google-drive.png"
//@ts-ignore
import SortIcon from "../../../../../assets/admin_icons/sort.svg"
import { releases } from "../../../../../apis"
import { toast } from "react-toastify"
import { errorFilledToastOptions, infoFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
import { LabelCampBulkTrackUpload, LCReleaseFormData } from "../../../../../types"
import { mapToCamelCase } from "../../../../../utility"

type BulkUploadAudioModalProps = {
  open: boolean
  setOpen: (open: boolean) => void
  productId: string
  tracks: LCReleaseFormData[]
  setProcessingTrackIds: (trackIds: string[]) => void
  loading: boolean
  setLoading: (loading: boolean) => void
  fetchProduct: (productId: string, trackOfferIncluded: boolean) => void
  setDriveBulkUpload: (driveBulkUpload?: LabelCampBulkTrackUpload) => void
  isTakedown: boolean
}

enum UPLOAD_OPTIONS {
  CHOOSE_FROM_FILES = "choose_from_files",
  UPLOAD_FROM_DRIVE = "upload_from_drive"
}

enum METHOD_OPTIONS {
  CREATE_NEW_TRACKS = "create_new_tracks",
  MATCH_WITH_EXISTED_ACR = "match_with_existed_acr",
  MATCH_WITH_EXISTED_MANUEL = "match_with_existed_manuel"
}

const BulkUploadAudioModal: FC<BulkUploadAudioModalProps> = (props) => {
  const [selectedUpload, setSelectedUpload] = useState<UPLOAD_OPTIONS>()
  const [selectedMethod, setSelectedMethod] = useState(METHOD_OPTIONS.CREATE_NEW_TRACKS)
  const [files, setFiles] = useState<File[]>([])
  const [driveLink, setDriveLink] = useState("")
  const [fileNameTrackMap, setFileNameTrackMap] = useState<{ [key: string]: any }>({})

  const { open, setOpen, productId, tracks, setProcessingTrackIds, loading, setLoading, fetchProduct } = props

  const onClose = () => {
    setOpen(false)
    setFiles([])
    setDriveLink("")
    setSelectedMethod(METHOD_OPTIONS.CREATE_NEW_TRACKS)
    setFileNameTrackMap({})
  }

  useEffect(() => {
    setFiles([])
    setDriveLink("")
    setSelectedMethod(METHOD_OPTIONS.CREATE_NEW_TRACKS)
    setFileNameTrackMap({})
  }, [selectedUpload])

  const submitFiles = () => {
    if (selectedUpload === UPLOAD_OPTIONS.UPLOAD_FROM_DRIVE) {
      setLoading(true)
      releases(
        `/release/label_camp_drive_import_track_audio/?product_id=${productId}&method_option=${selectedMethod}&drive_link=${driveLink}&track_number=${tracks.length}`,
        "post"
      )
        .then((resp) => {
          setLoading(false)
          if (resp.data.result) {
            toast.success(
              lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.started_audio_upload_from_drive,
              {
                ...successFilledToastOptions,
                autoClose: 10000
              }
            )
            const driveBulkUpload: LabelCampBulkTrackUpload = mapToCamelCase(resp.data.result)
            props.setDriveBulkUpload(driveBulkUpload)
            onClose()
          } else if (
            resp.data.error_code &&
            lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.hasOwnProperty(resp.data.error_code)
          ) {
            toast.info(
              //@ts-ignore
              lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal[resp.data.error_code],
              { ...infoFilledToastOptions, autoClose: 10000 }
            )
          } else {
            toast.error(
              lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.error_uploading_assets,
              errorFilledToastOptions
            )
          }
        })
        .catch((error) => {
          toast.error(
            lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.error_uploading_assets,
            errorFilledToastOptions
          )
          setLoading(false)
        })
    } else {
      try {
        const formData = new FormData()
        files.forEach((file: any) => {
          formData.append("files", file)
        })
        formData.append("file_name_track_map", JSON.stringify(fileNameTrackMap))

        setLoading(true)
        releases(
          `/release/label_camp_bulk_upload_track_audio/?product_id=${productId}&upload_option=${selectedUpload}&method_option=${selectedMethod}&track_number=${tracks.length}`,
          "post",
          formData
        )
          .then((resp) => {
            setLoading(false)
            if (
              selectedMethod === METHOD_OPTIONS.CREATE_NEW_TRACKS ||
              selectedMethod === METHOD_OPTIONS.MATCH_WITH_EXISTED_MANUEL
            ) {
              if (resp.data.error_list.length > 0) {
                toast.error(
                  `${
                    lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.error_creating_tracks
                  } ${resp.data.error_list.join(", ")} assets`,
                  errorFilledToastOptions
                )
              }
              if (resp.data.error_list.length === 0 && resp.data.success_id_list.length === 0) {
                toast.error(
                  lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.error_uploading_assets,
                  errorFilledToastOptions
                )
              }
              if (selectedMethod === METHOD_OPTIONS.CREATE_NEW_TRACKS) {
                fetchProduct(productId, false)
              }

              if (resp.data.success_id_list.length > 0) {
                setProcessingTrackIds(resp.data.success_id_list)
                toast.success(
                  `${resp.data.success_id_list.length} ${lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.tracks_created_and_upload_started}`,
                  successFilledToastOptions
                )
                onClose()
              }
            } else {
              if (resp.data.result) {
                const driveBulkUpload: LabelCampBulkTrackUpload = mapToCamelCase(resp.data.result)
                props.setDriveBulkUpload(driveBulkUpload)
                toast.success(
                  lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.started_audio_match,
                  {
                    ...successFilledToastOptions,
                    autoClose: 10000
                  }
                )
                onClose()
              } else if (
                resp.data.error_code &&
                lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.hasOwnProperty(
                  resp.data.error_code
                )
              ) {
                toast.info(
                  //@ts-ignore
                  lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal[resp.data.error_code],
                  { ...infoFilledToastOptions, autoClose: 10000 }
                )
              } else {
                toast.error(
                  lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.error_uploading_assets,
                  errorFilledToastOptions
                )
              }
            }
          })
          .catch((error) => {
            toast.error(
              lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.error_uploading_assets,
              errorFilledToastOptions
            )
            setLoading(false)
          })
      } catch (error: any) {
        toast.error(
          lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.error_uploading_assets,
          errorFilledToastOptions
        )
        setLoading(false)
        fetchProduct(productId, false)
      }
    }
  }

  const handleFileChange = (e: any) => {
    if (!e.target.files) {
      return
    }
    const files: File[] = Array.from(e.target.files)
    setFiles(files)

    const fileNameTrackMap: any = {}
    files.forEach((file, index) => {
      fileNameTrackMap[file.name] = tracks[index]
        ? {
            id: tracks[index].data.id,
            title: tracks[index].data.attributes["title"],
            isrc: tracks[index].data.attributes["isrc-code"]
          }
        : {}
    })
    setFileNameTrackMap(fileNameTrackMap)
  }

  const getFileOrder = (trackId: string) => {
    if (!trackId) return ""

    const trackIndex = tracks.findIndex((track) => track.data.id === trackId)
    return trackIndex !== -1 ? (trackIndex + 1).toString() : ""
  }

  const onSort = (fileIndex: number) => {
    const expectedOrder = window.prompt(
      lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.enter_track_order
    )
    if (!expectedOrder) return

    const newPosition = parseInt(expectedOrder, 10) - 1 // convert to 0-based index
    if (isNaN(newPosition) || newPosition < 0 || newPosition >= files.length) {
      toast.error(
        lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.invalid_position,
        errorFilledToastOptions
      )
      return
    }

    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles]
      const [movedFile] = updatedFiles.splice(fileIndex, 1) // remove the file
      updatedFiles.splice(newPosition, 0, movedFile) // insert at new position
      return updatedFiles
    })
  }

  const processFileOrder = (fileName: string) => {
    const order = window.prompt(lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.enter_track_order)
    if (!order) return

    const orderInt = parseInt(order)
    if (!tracks[orderInt - 1]) return
    const trackId = tracks[orderInt - 1].data.id

    setFileNameTrackMap((prevMap) => {
      // Find if any other file already has this trackId
      const existingFile = Object.keys(prevMap).find((key) => prevMap[key]?.["id"] === trackId && key !== fileName)

      // Create new map to return
      const newMap: any = { ...prevMap }

      if (existingFile) {
        // Swap the values
        newMap[existingFile] = prevMap[fileName] || ""
      }

      // Set the new trackId for the current file
      newMap[fileName] = {
        id: trackId,
        title: tracks[orderInt - 1].data.attributes["title"],
        isrc: tracks[orderInt - 1].data.attributes["isrc-code"]
      }

      return newMap
    })
  }

  const renderFiles = () => {
    if (selectedMethod === METHOD_OPTIONS.MATCH_WITH_EXISTED_MANUEL) {
      return (
        <div className="pt-4 pb-4" style={{ maxHeight: 360, overflowY: "scroll" }}>
          {files.length > 0 ? (
            <div className="flex w-full max-h-[400px] overflow-y-auto border rounded-lg shadow-sm bg-white">
              {/* Left side - Files */}
              <div className="w-[50%] border-r p-4">
                <KSpan
                  color="#111"
                  fontStyle="italic"
                  text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.uploaded_files}
                />
                {files.map((file, index) => (
                  <div
                    key={`files_${index}`}
                    className="flex items-center justify-between gap-3 p-2 mb-2 rounded-md hover:bg-gray-50 border border-transparent hover:border-gray-200 cursor-pointer transition-all duration-150"
                  >
                    <div className="flex">
                      <KSpan text={file.name} />
                    </div>

                    {/* Optional input for manual matching */}
                    <KButton
                      width={"20px"}
                      height="37px"
                      onClick={() => processFileOrder(file.name)}
                      background="white"
                      text={getFileOrder(fileNameTrackMap[file.name]?.["id"] || "")}
                    />
                  </div>
                ))}
              </div>

              {/* Right side - Existing tracks */}
              <div className="w-[50%] p-4">
                <KSpan
                  color="#111"
                  fontStyle="italic"
                  text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.existed_tracks}
                />
                {tracks.map((track, index) => (
                  <div
                    style={{ height: 55 }}
                    key={`tracks_${index}`}
                    className="flex items-center gap-2 p-2 mb-2 rounded-md hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-all duration-150"
                  >
                    <KSpan
                      text={`${index + 1}. ${track.data.attributes["title"]} (${track.data.attributes["isrc-code"]})`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-4 pt-4 pb-4">
              <KSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.no_file_chosen} />
            </div>
          )}
        </div>
      )
    }
    return (
      <div className="pt-4 pb-4" style={{ maxHeight: 360, overflowY: "scroll" }}>
        {files.length > 0 ? (
          <div>
            {files.map((file, index) => {
              return (
                <div className="flex gap-2 mt-2 items-center" key={`files_${index}`}>
                  {selectedMethod === METHOD_OPTIONS.CREATE_NEW_TRACKS && (
                    <KTooltip
                      width="80px"
                      position="bottom"
                      align="right"
                      children={
                        <img width={10} src={SortIcon} className="cursor-pointer" onClick={() => onSort(index)} />
                      }
                      content={
                        <KSpan
                          text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.click_to_reorder}
                        />
                      }
                    />
                  )}
                  <KSpan color="#111" text={`${index + 1}: `} />
                  <KSpan text={file.name} />
                </div>
              )
            })}
          </div>
        ) : (
          <div className="mb-4">
            <KSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.no_file_chosen} />
          </div>
        )}
      </div>
    )
  }

  const renderChooseFromFiles = () => {
    return (
      <div className="px-4 pt-2">
        <div>
          <label
            htmlFor="file-upload"
            style={{
              backgroundColor: "white",
              color: "black",
              padding: "6px 12px",
              borderRadius: "6px",
              border: "1px solid #11111124",
              cursor: "pointer",
              marginRight: "10px",
              fontFamily: "Inter"
            }}
          >
            {lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.choose_files}
          </label>
          <span id="file-name" style={{ color: "#666", fontSize: "0.9rem", fontFamily: "Inter" }}>
            {lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.upload_audio_asset}
          </span>
          <input
            id="file-upload"
            type="file"
            multiple
            onChange={(e: any) => {
              handleFileChange(e)
            }}
            accept=".wav, .flac"
            style={{ display: "none" }}
          />
        </div>
        <div className="mt-2">{renderFiles()}</div>
      </div>
    )
  }

  const renderUploadFromDrive = () => {
    return (
      <div className="pl-4 pr-4 pb-8">
        <div className="flex flex-col pt-2 pl-1">
          <KSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.enter_drive_link} />
          <KSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.accepted_formats} />
        </div>
        <div className="pt-4">
          <KInput
            onChange={(text) => {
              setDriveLink(text)
            }}
            placeholder="https://drive.google.com/drive/folders/xxxxxx"
            value={driveLink}
          />
        </div>
      </div>
    )
  }

  const renderMethodSelection = () => {
    return (
      <div className="p-4">
        <div className="flex items-center">
          <input
            style={{ accentColor: "black", marginTop: 2 }}
            className={"cursor-pointer"}
            type="radio"
            checked={selectedMethod === METHOD_OPTIONS.CREATE_NEW_TRACKS}
            onClick={() => setSelectedMethod(METHOD_OPTIONS.CREATE_NEW_TRACKS)}
          />
          <div className={"ml-2"}>
            <KSpan
              color="black"
              text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.create_new_tracks}
            />
          </div>
        </div>
        {selectedUpload === UPLOAD_OPTIONS.CHOOSE_FROM_FILES && (
          <div className="flex items-center mt-4">
            <input
              style={{ accentColor: "black", marginTop: 2 }}
              className={"cursor-pointer"}
              type="radio"
              checked={selectedMethod === METHOD_OPTIONS.MATCH_WITH_EXISTED_MANUEL}
              onClick={() => setSelectedMethod(METHOD_OPTIONS.MATCH_WITH_EXISTED_MANUEL)}
            />
            <div className={"ml-2"}>
              <KSpan
                color="black"
                text={
                  lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.match_existing_tracks_manual
                }
              />
            </div>
          </div>
        )}
        {props.isTakedown && (
          <div className="flex items-center mt-4">
            <input
              style={{ accentColor: "black", marginTop: 2 }}
              className={"cursor-pointer"}
              type="radio"
              checked={selectedMethod === METHOD_OPTIONS.MATCH_WITH_EXISTED_ACR}
              onClick={() => setSelectedMethod(METHOD_OPTIONS.MATCH_WITH_EXISTED_ACR)}
            />
            <div className={"ml-2"}>
              <KSpan
                color="black"
                text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.match_existing_tracks_acr}
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderLanding = () => {
    return (
      <div className="p-4">
        <div>
          <KSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.choose_upload_method} />
        </div>
        <div className="flex w-full justify-center pb-8 pt-4 gap-4">
          <div
            className="flex justify-center cursor-pointer hover:!bg-[#F3F3F3]"
            style={{ width: "50%", border: "1px solid #11111112", borderRadius: 5 }}
            onClick={() => setSelectedUpload(UPLOAD_OPTIONS.CHOOSE_FROM_FILES)}
          >
            <div className="py-10 gap-3 flex flex-col items-center">
              <img src={BulkUploadIconIcon} width={90} height={"auto"} />
              <KSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.choose_from_files} />
            </div>
          </div>
          <div
            className="flex justify-center cursor-pointer hover:!bg-[#F3F3F3]"
            style={{ width: "50%", border: "1px solid #11111112", borderRadius: 5 }}
            onClick={() => setSelectedUpload(UPLOAD_OPTIONS.UPLOAD_FROM_DRIVE)}
          >
            <div className="py-10 gap-3 flex flex-col items-center">
              <img src={GoogleDriveIconIcon} width={90} height={"auto"} />
              <KSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload_audio_modal.upload_from_drive} />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onClose={() => onClose()} className="relative z-50">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="mt-10 bg-white elev-b-sm rounded-lg overflow-hidden">
          <div
            className={`bg-gray-50 text-xs text-gray-500 font-medium 
              uppercase border-b flex px-4 py-1 mr-28 pr-28 flex justify-between`}
            style={{ width: "100%" }}
          >
            <KTitleSpan text={lang.catalog.asset_registry.tracks_and_assets.bulk_upload} fontSize={24} />
            <img className="rounded-full cursor-pointer" src="/admin_icons/close.svg" onClick={() => onClose()} />
          </div>
          {selectedUpload ? renderMethodSelection() : renderLanding()}
          {selectedUpload === UPLOAD_OPTIONS.CHOOSE_FROM_FILES && renderChooseFromFiles()}
          {selectedUpload === UPLOAD_OPTIONS.UPLOAD_FROM_DRIVE && renderUploadFromDrive()}
          <div className={`border-t flex justify-between py-2 pr-4`}>
            <div>
              {selectedUpload && (
                <KButton
                  width="100px"
                  onClick={() => setSelectedUpload(undefined)}
                  text={lang.catalog.asset_registry.back}
                  background="white"
                  textColor="black"
                  shadowDisabled
                />
              )}
            </div>
            <KButton
              width="100px"
              onClick={() => submitFiles()}
              disabled={loading || !selectedUpload || !selectedMethod || !(files.length !== 0 || driveLink)}
              text={lang.common.submit}
              background="black"
              textColor="white"
            />
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default BulkUploadAudioModal
